import { useEffect, useState } from "react";
import type { Locale } from "../lib/i18n";

const OPTIONS: { code: Locale; label: string }[] = [
  { code: "he", label: "עב" },
  { code: "en", label: "EN" },
];

interface Props {
  locale: Locale;
  setLocale: (l: Locale) => void;
  buttonClassName: string;
}

export function LocalePicker({ locale, setLocale, buttonClassName }: Props) {
  const [open, setOpen] = useState(false);
  const current = OPTIONS.find((o) => o.code === locale) ?? OPTIONS[0];

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === "Escape") setOpen(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  return (
    <div className="relative">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-haspopup="listbox"
        aria-expanded={open}
        aria-label="Select language"
        className={buttonClassName}
      >
        {current.label}
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <ul
            role="listbox"
            className="absolute end-0 top-full mt-2 z-50 min-w-[96px] glass-solid rounded-xl p-1.5 shadow-xl"
          >
            {OPTIONS.map((o) => {
              const active = o.code === locale;
              return (
                <li key={o.code} role="none">
                  <button
                    type="button"
                    role="option"
                    aria-selected={active}
                    onClick={() => { setLocale(o.code); setOpen(false); }}
                    className={`w-full text-start px-3 py-2 rounded-lg text-sm font-bold transition-colors ${
                      active ? "bg-white/5" : "hover:bg-white/5"
                    }`}
                    style={{ color: active ? "var(--gold-400)" : "var(--text-secondary)" }}
                  >
                    {o.label}
                  </button>
                </li>
              );
            })}
          </ul>
        </>
      )}
    </div>
  );
}
